import WebAppAPI from '../utils/WebAppAPI';
import useAuthStore from '../store/Auth';
import useChatsStore from '../store/Chats';
import { v4 as uuidv4 } from 'uuid';
import type { ChatObject } from '@/types';

/* eslint-disable @typescript-eslint/no-explicit-any */

// ── Helpers ───────────────────────────────────────────────────────────────────

const getOrgId = (): string => {
  const { organization } = useAuthStore.getState();
  return (organization as any)?.id as string;
};

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AssistantThread {
  _id: string;
  project_ids: string[];
  last_message_at: string;
  created_at: string;
  message_preview: string;
}

export interface AssistantMessage {
  _id: string;
  role: 'user' | 'assistant';
  text: string;
  project_ids?: string[];
  created_at: string;
  updated_at: string;
}

export interface AssistantApiResponse {
  success: boolean;
  thread_id: string;
  message_id: string;
  answer: string;
  intent?: string;
  strategy?: string;
  error?: string;
}

export interface PaginationMeta {
  page: number;
  /** Backend returns this as `pages`; older code reads `totalPages`. */
  pages?: number;
  totalPages?: number;
  total?: number;
}

/** Read the total page count from either `pages` (current) or `totalPages` (legacy). */
const getTotalPages = (meta?: PaginationMeta): number | undefined =>
  meta?.pages ?? meta?.totalPages;

export interface SendAssistantMessageParams {
  message: string;
  threadId?: string | null;
  projectIds?: string[];
  projectCodeNames?: string[];
}

// ── Error message mapping ─────────────────────────────────────────────────────

const HTTP_ERROR_MESSAGES: Record<number, string> = {
  401: 'Your session has expired. Please refresh the page.',
  403: "You don't have permission to use the assistant.",
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'An internal server error occurred. Please try again.',
  502: 'Bad gateway. The server may be restarting — please try again.',
  503: 'The server is temporarily unavailable. Please try again in a moment.',
  504: 'The server timed out. Please try again.',
};

function getHttpErrorMessage(status: number): string {
  return (
    HTTP_ERROR_MESSAGES[status] ??
    (status >= 500
      ? `Server error (${status}). Please try again.`
      : `Request failed (${status}). Please check your input.`)
  );
}

// ── Stream handler ────────────────────────────────────────────────────────────

export interface StreamError {
  message: string;
  /** Recommended UI action for this error. */
  action: 'retry' | 'new-chat';
}

/**
 * Maps a backend `error` string to a UI action. Backend-level failures that
 * indicate the chat can't be continued (e.g. inaccessible projects) require
 * starting a new chat; everything else is treated as retryable.
 *
 * NOTE: this is a text match by necessity — if/when the backend ships a stable
 * machine-readable code, prefer that over the message substring.
 */
function classifyBackendError(message: string): StreamError['action'] {
  const lower = message.toLowerCase();
  if (
    lower.includes('no longer accessible') ||
    lower.includes('start a new chat')
  ) {
    return 'new-chat';
  }
  return 'retry';
}

/**
 * Scans an arbitrary payload (string body, NDJSON, or already-parsed object)
 * for a backend failure envelope: `{ success: false, error: "..." }`.
 * Returns a StreamError if found, otherwise null. Used to surface structured
 * backend errors regardless of HTTP status — the same shape can arrive on a
 * 200 (streaming success path) or a 400 (caught as a thrown axios error).
 */
function extractBackendError(payload: unknown): StreamError | null {
  if (payload == null) return null;

  const tryParse = (raw: unknown): StreamError | null => {
    if (raw && typeof raw === 'object') {
      const obj = raw as Partial<AssistantApiResponse>;
      if (obj.success === false && typeof obj.error === 'string') {
        return {
          message: obj.error,
          action: classifyBackendError(obj.error),
        };
      }
    }
    return null;
  };

  if (typeof payload === 'object') {
    return tryParse(payload);
  }

  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (!trimmed) return null;
    // Try whole-string parse first, then fall back to NDJSON line-by-line.
    try {
      return tryParse(JSON.parse(trimmed));
    } catch {
      /* not whole-string JSON */
    }
    for (const line of trimmed.split('\n')) {
      const lineTrimmed = line.trim();
      if (!lineTrimmed) continue;
      try {
        const found = tryParse(JSON.parse(lineTrimmed));
        if (found) return found;
      } catch {
        /* skip non-JSON line */
      }
    }
  }

  return null;
}

/**
 * Reads a streamed response body and calls onChunk as answer text accumulates,
 * then calls onComplete with the final answer and thread_id (or a streamError
 * when the backend returned `success: false`).
 *
 * Supports three streaming formats:
 *  1. NDJSON  — each newline-delimited line is a complete JSON object
 *  2. Single JSON — full response object split across multiple network chunks
 *  3. Plain text — raw text chunks accumulated directly as the answer
 */
async function consumeStream(
  responseText: string,
  onChunk: (accumulatedAnswer: string) => void,
  onComplete: (
    answer: string,
    threadId: string | null,
    streamError: StreamError | null
  ) => void
): Promise<void> {
  let accumulatedAnswer = '';
  let resolvedThreadId: string | null = null;
  let streamError: StreamError | null = null;

  const processLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    try {
      const parsed: AssistantApiResponse = JSON.parse(trimmed);

      // Backend-level failure path: { success: false, error: "..." }
      const backendError = extractBackendError(parsed);
      if (backendError) {
        streamError = backendError;
        return;
      }

      if (typeof parsed.answer === 'string' && parsed.answer) {
        accumulatedAnswer = parsed.answer;
      }
      if (parsed.thread_id) {
        resolvedThreadId = parsed.thread_id;
      }
    } catch {
      accumulatedAnswer += trimmed;
    }

    onChunk(accumulatedAnswer);
  };

  // Process NDJSON response
  const lines = responseText.split('\n');
  for (const line of lines) {
    processLine(line);
  }

  onComplete(accumulatedAnswer, resolvedThreadId, streamError);
}

// ── Send message (with streaming) ─────────────────────────────────────────────

export const sendAssistantMessage = async (
  params: SendAssistantMessageParams
): Promise<void> => {
  const { message, threadId = null, projectIds = [], projectCodeNames = [] } = params;
  const orgId = getOrgId();

  const {
    addChatObject,
    updateChatObject,
    addToChatHistory,
    initializeChat,
    updateSelectedChatId,
    renameChat,
  } = useChatsStore.getState();

  // For new threads, create a temporary local ID for optimistic UI.
  // For existing threads, reuse the backend thread_id as sessionId.
  const sessionId = threadId ?? uuidv4();

  // Store project IDs on the chat so retry can retrieve them later.
  // We normalise each id into a ChatProjectKey shape (key = id = the MongoDB _id string).
  const projectKeyObjects = projectIds.map((id) => ({ key: id, id }));
  initializeChat(sessionId, null, null, null, projectKeyObjects, '', null, null, null, null, 'single', null, projectCodeNames);
  const chatObjectId = addChatObject(sessionId, message);
  updateSelectedChatId(sessionId);

  const body: Record<string, unknown> = {
    message,
    project_ids: projectIds,
    model_type: 'openai',
  };
  if (threadId) {
    body.thread_id = threadId;
  }

  try {
    const response = await WebAppAPI().post(
      `organizations/${orgId}/project/assistant`,
      body,
      { responseType: 'text' }
    );

    const responseText = response.data as string;
    await consumeStream(
      responseText,
      // onChunk: update UI with partial answer as stream progresses
      (accumulatedAnswer) => {
        updateChatObject(sessionId, chatObjectId, {
          aiResponse: accumulatedAnswer || null,
        });
      },
      // onComplete: finalize the message
      (finalAnswer, resolvedThreadId, streamError) => {
        // Backend reported an unrecoverable / actionable error in-body
        // (HTTP 200 with { success: false, error }). Surface as an error
        // bubble with the appropriate CTA hint.
        if (streamError) {
          updateChatObject(sessionId, chatObjectId, {
            aiResponse: streamError.message,
            responseComplete: true,
            isError: true,
            errorAction: streamError.action,
          });
          return;
        }

        const answer = finalAnswer || 'No response received.';

        updateChatObject(sessionId, chatObjectId, {
          aiResponse: answer,
          responseComplete: true,
          threadId: resolvedThreadId,
        });

        addToChatHistory(sessionId, message, answer);

        // If backend assigned a real thread_id different from our temp sessionId,
        // migrate the chat entry so selectedChatId points to the canonical thread_id.
        if (resolvedThreadId && resolvedThreadId !== sessionId) {
          renameChat(sessionId, resolvedThreadId);
          // renameChat also updates selectedChatId when it matches sessionId
        }
      }
    );
  } catch (error) {
    const statusCode = (error as any)?.response?.status as number | undefined;
    const responseBody = (error as any)?.response?.data;

    console.error('[AssistantService] sendAssistantMessage error:', error);

    // Prefer the structured backend error envelope when present, regardless
    // of HTTP status. The "projects no longer accessible" case comes back as
    // a 400 with { success: false, error: "..." } in the body.
    const backendError = extractBackendError(responseBody);
    if (backendError) {
      updateChatObject(sessionId, chatObjectId, {
        aiResponse: backendError.message,
        responseComplete: true,
        isError: true,
        errorAction: backendError.action,
        errorCode: statusCode,
      });
      return;
    }

    const message = statusCode
      ? getHttpErrorMessage(statusCode)
      : 'Could not reach the server. Check your connection and try again.';

    updateChatObject(sessionId, chatObjectId, {
      aiResponse: message,
      responseComplete: true,
      isError: true,
      errorCode: statusCode,
    });
  }
};

// ── Retry a failed message ────────────────────────────────────────────────────

/**
 * Removes the failed ChatObject from the conversation, then re-sends the
 * original message in the same thread. The UI snaps back to loading state
 * and the fresh response appears without leaving a ghost error bubble.
 */
export const retryAssistantMessage = async (
  chatId: string,
  chatObjectId: string,
  originalMessage: string
): Promise<void> => {
  const { deleteChatObject, getChatData } = useChatsStore.getState();

  // Derive project IDs from whatever was stored with the chat.
  // projectKeys are stored as { key: id, id } by sendAssistantMessage, so
  // p.id is the canonical MongoDB _id. Fall back to p._id / p.key for safety.
  const chatData = getChatData(chatId);
  const projectIds = (chatData?.projectKeys ?? [])
    .map((p: any) => (p.id || p._id || p.key) as string)
    .filter(Boolean);

  // Determine if chatId is already a real backend thread_id.
  // A real thread_id will have been set on at least one successful chatObject.
  const hasRealThread = (chatData?.conversation ?? []).some(
    (c) => c.threadId && c.id !== chatObjectId
  );

  // Remove the failed message bubble first
  deleteChatObject(chatId, chatObjectId);

  // Re-send — pass threadId only if the thread exists on the backend
  await sendAssistantMessage({
    message: originalMessage,
    threadId: hasRealThread ? chatId : null,
    projectIds,
  });
};

// ── Fetch threads ─────────────────────────────────────────────────────────────

export interface ThreadsResponse {
  data: AssistantThread[];
  meta?: PaginationMeta;
}

export const fetchAssistantThreads = async (
  page = 1
): Promise<ThreadsResponse> => {
  const orgId = getOrgId();
  const response = await WebAppAPI().get(
    `organizations/${orgId}/project/assistant/threads`,
    { params: { page } }
  );
  return response.data;
};

// ── Find an existing thread by project IDs (order-independent) ────────────────

const sameProjectSet = (a: string[] = [], b: string[] = []): boolean => {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  return b.every((id) => setA.has(id));
};

/**
 * Walks the paginated threads list looking for a thread whose `project_ids`
 * exactly match the given set (order doesn't matter). Returns the thread `_id`
 * if found, else `null`.
 */
export const findThreadByProjectIds = async (
  projectIds: string[]
): Promise<string | null> => {
  if (!projectIds.length) return null;

  let page = 1;
  // hard cap to avoid runaway loops if meta is missing
  const MAX_PAGES = 50;

  while (page <= MAX_PAGES) {
    const { data, meta } = await fetchAssistantThreads(page);
    const match = (data ?? []).find((t) =>
      sameProjectSet(t.project_ids ?? [], projectIds)
    );
    if (match) return match._id;

    const totalPages = getTotalPages(meta);
    if (!totalPages || page >= totalPages) return null;
    page++;
  }

  return null;
};

// ── Delete a thread ───────────────────────────────────────────────────────────

export const deleteAssistantThread = async (
  threadId: string
): Promise<{ success: boolean }> => {
  if (!threadId) {
    throw new Error('deleteAssistantThread called without a threadId');
  }
  const orgId = getOrgId();
  const response = await WebAppAPI().delete(
    `organizations/${orgId}/project/assistant/threads/${threadId}`
  );
  return response.data;
};

// ── Fetch messages for a thread ───────────────────────────────────────────────

export const fetchThreadMessages = async (
  threadId: string,
  page = 1
): Promise<{ items: AssistantMessage[]; meta?: PaginationMeta }> => {
  if (!threadId) {
    throw new Error('fetchThreadMessages called without a threadId');
  }
  const orgId = getOrgId();
  const response = await WebAppAPI().get(
    `organizations/${orgId}/project/assistant/threads/${threadId}/messages`,
    { params: { page } }
  );
  return response.data;
};

// ── Load a backend thread into the local store ────────────────────────────────

/**
 * Pairs a page of AssistantMessages (user → assistant in chronological order)
 * into ChatObject form. Returns ready-to-store ChatObjects.
 */
const pairMessagesIntoChatObjects = (
  items: AssistantMessage[],
  threadId: string
): ChatObject[] => {
  const result: ChatObject[] = [];
  for (let i = 0; i < items.length; i++) {
    const msg = items[i];
    if (msg.role !== 'user') continue;

    const next = items[i + 1];
    const aiResponse = next?.role === 'assistant' ? next.text : null;

    result.push({
      id: uuidv4(),
      threadId,
      userQuestion: msg.text,
      aiResponse: aiResponse ?? 'No response.',
      timestamp: msg.created_at,
      responseComplete: true,
    });

    // Skip the assistant message we just consumed
    if (aiResponse !== null) i++;
  }
  return result;
};

export const loadThreadIntoStore = async (
  threadId: string,
  projectCodeNames: string[] = []
): Promise<void> => {
  const {
    deleteChat,
    initializeChat,
    addChatObject,
    updateChatObject,
    updateSelectedChatId,
    setChatPagination,
  } = useChatsStore.getState();

  // Always start fresh — evict any stale cached version
  deleteChat(threadId);

  const { items, meta } = await fetchThreadMessages(threadId, 1);

  // Extract project IDs from the messages (usually in user messages)
  const projectIds = Array.from(
    new Set(items.flatMap((msg) => msg.project_ids ?? []).filter(Boolean))
  );
  const projectKeyObjects = projectIds.map((id) => ({ key: id, id }));

  initializeChat(threadId, null, null, null, projectKeyObjects, '', null, null, null, null, 'single', null, projectCodeNames);

  // Pair messages: user → assistant
  for (let i = 0; i < items.length; i++) {
    const msg = items[i];
    if (msg.role !== 'user') continue;

    const next = items[i + 1];
    const aiResponse = next?.role === 'assistant' ? next.text : null;

    const chatObjectId = addChatObject(threadId, msg.text);
    updateChatObject(threadId, chatObjectId, {
      aiResponse: aiResponse ?? 'No response.',
      responseComplete: true,
      threadId,
      timestamp: msg.created_at,
    });

    // Skip the assistant message we just consumed
    if (aiResponse !== null) i++;
  }

  // Track pagination state so scroll-to-top can fetch the next (older) page.
  // hasMore is true when there's more than one page; if meta is absent, fall back to false.
  const totalPages = getTotalPages(meta);
  const hasMore = !!(totalPages && totalPages > 1);
  setChatPagination(threadId, 1, hasMore);

  updateSelectedChatId(threadId);
};

// ── Load the next (older) page of messages and prepend to the conversation ────

/**
 * Fetches the next page of older messages for the given thread and prepends
 * them to the existing conversation. Returns true if any messages were added.
 *
 * The function is a no-op (returns false) when:
 *  • the chat has no recorded pagination state,
 *  • the previous load reported no more pages, or
 *  • the next page comes back empty.
 */
export const loadMoreThreadMessages = async (
  threadId: string
): Promise<boolean> => {
  if (!threadId) return false;

  const { getChatData, prependChatObjects, setChatPagination } =
    useChatsStore.getState();

  const chatData = getChatData(threadId);
  if (!chatData) return false;

  const currentPage = chatData.messagesPage ?? 1;
  const hasMore = chatData.hasMoreMessages ?? false;
  if (!hasMore) return false;

  const nextPage = currentPage + 1;
  const { items, meta } = await fetchThreadMessages(threadId, nextPage);

  const newChatObjects = pairMessagesIntoChatObjects(items, threadId);

  if (newChatObjects.length > 0) {
    prependChatObjects(threadId, newChatObjects);
  }

  // Update pagination state. Prefer totalPages from meta when available;
  // otherwise infer from the page length (empty page = no more).
  const totalPages = getTotalPages(meta);
  const newHasMore = totalPages
    ? nextPage < totalPages
    : newChatObjects.length > 0;
  setChatPagination(threadId, nextPage, newHasMore);

  return newChatObjects.length > 0;
};
